import React, { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { HelpCircle } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";

import { Card, CardContent } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableRow,
} from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { 
  Tooltip, 
  TooltipContent, 
  TooltipProvider, 
  TooltipTrigger 
} from "@/components/ui/tooltip";
 

import { FundRawRecord } from "@/components/funds-raw/types";

import { useAddToPortfolio } from "@/hooks/portfolio/useAddToPortfolio";
import { isSmallViewport } from "@/lib/utils";
import { MobileViewportWarning } from '@/components/ui/MobileViewportWarning';

// Import the query functions and types from the extracted file
import { resetQueryStrategy, SimpleFundRecord } from "./companyFundsQueries";
import { useCompanyFundsQueries } from "./useCompanyFundsQueries";
import CompanyFundsTableHeader from "./CompanyFundsTableHeader";
import CompanyFundsLoading from "./CompanyFundsLoading";
import CompanyFundsRow from "./CompanyFundsRow";
import CompanyFundsControls from "./CompanyFundsControls";
import CompanyFundsEmpty from "./CompanyFundsEmpty";
import { useCompanyFundsTableState } from "./useCompanyFundsTableState";
import CompanyFundsError from "./CompanyFundsError";
import { useCompanyFundsActions } from "./useCompanyFundsActions";

interface CompanyFundsTableProps {
  fundId: string;
  companyId: string;
  companyName: string;
  companyWebsiteUrl: string;
  onAddToPortfolio?: (fundId: string) => void;
  onFavorite?: (fundId: string) => void;
  onAddToWatchlist?: (fundId: string) => void;
  refetchFundsForCompany?: () => void;
}

function CompanyFundsTable({
  fundId,
  companyId,
  companyName,
  companyWebsiteUrl,
  onAddToPortfolio: externalAddToPortfolio,
  onFavorite: externalFavorite,
  onAddToWatchlist: externalAddToWatchlist,
  refetchFundsForCompany,
}: CompanyFundsTableProps) {
  // Check if we're on a fund profile page to avoid duplicate dialogs
  const location = useLocation();
  const isFundProfilePage = location.pathname.includes('/funds/');
  // Provider-based portfolio dialog
  const { openFundsRaw } = useAddToPortfolio();
  const [isSmallScreen, setIsSmallScreen] = React.useState(() => isSmallViewport(480));
  
  // Pagination state
  const {
    currentPage,
    rowsPerPage,
    totalPages,
    totalCount,
    pageInputValue,
    setPaginationFromCount,
    ensurePageWithinBounds,
    handlePageChange,
    handleRowsPerPageChange,
    handlePageInputChange,
    handlePageInputBlur,
  } = useCompanyFundsTableState();
  
  // Reset pagination and strategy when props change
  useEffect(() => {
    resetQueryStrategy();
    handlePageChange(1);
  }, [fundId, companyId, companyName, companyWebsiteUrl]);
  
  // Check for small viewport
  useEffect(() => {
    const checkViewport = () => {
      setIsSmallScreen(isSmallViewport(480));
    };
    
    checkViewport();
    window.addEventListener('resize', checkViewport);
    
    return () => window.removeEventListener('resize', checkViewport);
  }, []);
  
  // First fetch the fund details to get company information if needed
  const { fundDetailsQuery, companyFundsQuery } = useCompanyFundsQueries({
    fundId,
    companyId,
    companyName,
    companyWebsiteUrl,
    currentPage,
    rowsPerPage,
  });
  
  // Update pagination state when we get new data
  useEffect(() => {
    if (companyFundsQuery.data) {
      const count = companyFundsQuery.data.count || 0;
      setPaginationFromCount(count);
      ensurePageWithinBounds(count);
    }
  }, [companyFundsQuery.data, rowsPerPage, currentPage]);
  
  // Retry function for failed queries
  const handleRetry = () => {
    resetQueryStrategy();
    companyFundsQuery.refetch();
  };
  
  // Get the funds from the query result (may be undefined during loading)
  const funds = companyFundsQuery.data?.data || [];
  // Map to FundRawRecord for rendering compatibility
  const fundRecords = React.useMemo(() => {
    return (funds || []).map((fund: SimpleFundRecord) => {
      const managementFeeNumber = fund.management_fee ? Number(fund.management_fee) : null;
      return {
        ...fund,
        sa_asset_class: fund.asset_class || null,
        sa_sub_asset_class: null,
        performance_ytd: null,
        performance_1yr: null,
        performance_3yr: null,
        performance_5yr: null,
        performance_inception: null,
        performance_inception_annual: null,
        management_fee: managementFeeNumber,
      } as FundRawRecord;
    });
  }, [funds]);
  // Action handlers
  const { handleAddToPortfolio, handleFavorite, handleWatchlist } = useCompanyFundsActions({
    fundRecords,
    openFundsRaw,
    externalAddToPortfolio,
    externalFavorite,
    externalAddToWatchlist,
    refetchFundsForCompany,
  });

  // Render loading state
  if (companyFundsQuery.isLoading || fundDetailsQuery.isLoading) {
    return (
      <Card className="mt-4">
        <CardContent className="p-6">
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
            className="space-y-2"
          >
            <Skeleton className="h-4 w-[250px]" />
            <Skeleton className="h-4 w-[200px]" />
          </motion.div>
          <motion.div 
            className="mt-4 space-y-2"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3, delay: 0.1 }}
          >
            {Array.from({ length: 3 }).map((_, index) => (
              <motion.div 
                key={index} 
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.2, delay: 0.1 * index }}
              >
                <Skeleton className="h-12 w-full" />
              </motion.div>
            ))}
          </motion.div>
        </CardContent>
      </Card>
    );
  }

  // Render error state
  if (companyFundsQuery.error || fundDetailsQuery.error) {
    console.error("Error fetching related funds:", companyFundsQuery.error || fundDetailsQuery.error);
    return <CompanyFundsError onRetry={handleRetry} />;
  }
  
  // Render empty state
  if ((funds?.length || 0) === 0) {
    return <CompanyFundsEmpty />;
  }

  return (
    <>
      <div className="flex items-center justify-between mt-6 mb-3">
        <h3 className="text-lg font-medium">Related Funds</h3>
        <div className="text-sm text-muted-foreground">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger>
                <div className="flex items-center gap-1">
                  <span>Other funds from the same company</span>
                  <HelpCircle className="h-4 w-4" />
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <div>These funds are managed by the same company as the current fund.</div>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>
      <Card className="mt-2">
        <CardContent>
          {isSmallScreen ? (
            <MobileViewportWarning 
              title="Small Screen Detected"
              message="This related funds table is best viewed on larger screens. Consider rotating your device to landscape orientation or using a device with a larger screen for optimal viewing experience."
              className="mb-4"
            />
          ) : (
            <>
              <CompanyFundsControls
                currentPage={currentPage}
                totalPages={totalPages}
                pageInputValue={pageInputValue}
                onPrev={() => handlePageChange(Math.max(1, currentPage - 1))}
                onNext={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
                onPageInputChange={handlePageInputChange}
                onPageInputBlur={handlePageInputBlur}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleRowsPerPageChange}
                totalCount={totalCount}
                isLoading={companyFundsQuery.isLoading}
              />
              
              <div className="rounded-md border relative">
                {/* Fixed header section outside of scrollable area */}
                <CompanyFundsTableHeader />
                
                {/* Scrollable body section */}
                <div className="overflow-auto" style={{ maxHeight: "calc(100vh - 350px)" }}>
                  <Table>
                    <TableBody>
                    {companyFundsQuery.isLoading ? (
                      <CompanyFundsLoading rows={5} />
                    ) : (fundRecords?.length || 0) === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} className="h-24 text-center">
                          No funds found for this company.
                        </TableCell>
                      </TableRow>
                    ) : (
                      <AnimatePresence>
                        {fundRecords.map((fund, index) => (
                          <CompanyFundsRow
                            key={fund.id}
                            fund={fund}
                            index={index}
                            onAddToPortfolio={(id: string) => handleAddToPortfolio(id, fund)}
                            onWatchlist={handleWatchlist}
                            onFavorite={handleFavorite}
                          />
                        ))}
                      </AnimatePresence>
                    )}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
      
      {/* Portfolio dialog is provided globally by FundsPortfolioDialogProvider */}
    </>
  );
}

export default CompanyFundsTable;
